import { normalizeSector } from "../../utils/billingPricing";
import { CITAS_CONEXT_SECTORS } from "../../../utils/conextSenderName";

// Clave de sector para etiqueta/número de reserva. normalizeSector no colapsa
// "peluqueria_unisex" (el id real del selector del simulador) → "peluqueria".
function sectorKey(sector) {
  const key = normalizeSector(sector || "");
  return key === "peluqueria_unisex" ? "peluqueria" : key;
}

// Titular + CTA del cartel según el sector normalizado.
// normalizeSector colapsa bares_deportes/food_orders/restauración → "restauracion".
const COPY_BY_SECTOR = {
  lavadero: {
    headline: "¡Reserva tu lavado al instante!",
    pill: "Pídelo por WhatsApp",
  },
  autoescuela: {
    headline: "¡Reserva tu clase al instante!",
    pill: "Pídela por WhatsApp",
  },
  restauracion: {
    headline: "¡Reserva tu mesa al instante!",
    pill: "Resérvala por WhatsApp",
  },
};

const DEFAULT_COPY = {
  headline: "¡Reserva tu cita al instante!",
  pill: "Pídela por WhatsApp",
};

// Cartel para sectores de PEDIDOS (food-orders): el cliente hace un pedido,
// no reserva una cita.
const FOOD_COPY = {
  headline: "¡Haz tu pedido al instante!",
  pill: "Pídelo por WhatsApp",
};
const FOOD_SECTORS = new Set([
  "comida_para_llevar", "pizzeria", "restaurantes", "distribucion",
  "bares_deportes", "bares_tematicos",
]);

// Devuelve { headline, pill } para el cartel según el sector.
export function getPosterCopy(sector) {
  if (FOOD_SECTORS.has(String(sector || "").toLowerCase().trim())) return FOOD_COPY;
  const key = normalizeSector(sector || "");
  return COPY_BY_SECTOR[key] || DEFAULT_COPY;
}

// Nombre del número de Conext por sector (p. ej. "ConextCRM Lavaderos").
// Las claves son el sector YA normalizado por normalizeSector (peluquerias →
// "peluqueria", etc.).
const SECTOR_LABEL = {
  lavadero: "Lavaderos",
  autoescuela: "Autoescuelas",
  restauracion: "Restaurantes",
  barberia: "Barberías",
  peluqueria: "Peluquerías",
  estetica: "Estética",
  // Peluquería canina comparte el número (y la WABA) de peluquerías.
  canina: "Peluquerías",
};

// Etiqueta del sector para "ConextCRM {label} oficial". "" si no se conoce.
export function getSectorLabel(sector) {
  return SECTOR_LABEL[sectorKey(sector)] || "";
}

// Nombre del número oficial por el que se pide cita: «Citas ConextCRM» para los sectores de citas
// sin número propio (su número desde el 26/9/2026); si no, «ConextCRM Estética», «ConextCRM Lavaderos»…
// `t` opcional (el componente pasa el suyo; los tests pueden omitirlo).
export function getOfficialNumberName(sector, t = (_key, def) => def) {
  if (CITAS_CONEXT_SECTORS.includes(sectorKey(sector))) {
    return t("mockBookingLinkButton.citasConextcrm", "Citas ConextCRM");
  }
  return `ConextCRM ${getSectorLabel(sector)}`.trim();
}

// Número de WhatsApp de Conext dedicado a citas, POR SECTOR. Espejo de
// c_sector_config[sector].booking_phone_e164 / booking_phone_number_id (lo que
// resuelve la lambda real c_getBookingLink). En el modelo actual el cliente NO
// conecta su propio número: las comunicaciones salen por el número de Conext de
// su sector. Claves = sector normalizado.
const SECTOR_BOOKING_NUMBER = {
  barberia: { e164: "+18455954957", phone_number_id: "1103283566208925" },
  peluqueria: { e164: "+19038653085", phone_number_id: "1155754894282649" },
  estetica: { e164: "+13072074986", phone_number_id: "1147822628415590" },
  // Canina reutiliza la WABA compartida de peluquerías (misma decisión que el
  // backend: CONEXT_SECTOR_PHONES.canina en c_sendTemplate).
  canina: { e164: "+19038653085", phone_number_id: "1155754894282649" },
  lavadero: { e164: "+12318795073", phone_number_id: "1135680079635856" },
};

// Código de reserva mock. En real es opaco y único por empresa
// (c_empresas.booking_code_short, 2 chars inicialmente).
export const MOCK_BOOKING_CODE = "T1";
const PREFILL_SUFFIX = "Hola, quiero pedir una cita";

/**
 * Devuelve la info del número de reserva de Conext del sector para el simulador,
 * o null si el sector no tiene número dedicado (entonces se usa el número demo
 * central). Mensaje y enlace replican el formato fallback de c_getBookingLink:
 *   wa.me/<digits>?text=(CODE no borrar) Hola, quiero pedir una cita
 */
export function getSectorBookingInfo(sector) {
  const entry = SECTOR_BOOKING_NUMBER[sectorKey(sector)];
  if (!entry) return null;
  const prefill = `(${MOCK_BOOKING_CODE} no borrar) ${PREFILL_SUFFIX}`;
  const digits = entry.e164.replace(/[^\d]/g, "");
  return {
    e164: entry.e164,
    phone_number_id: entry.phone_number_id,
    prefilled_message: prefill,
    deep_link_url: `https://wa.me/${digits}?text=${encodeURIComponent(prefill)}`,
  };
}

// Palabra de acción por sector para los mensajes de difusión/redes.
const ACTION_BY_SECTOR = {
  lavadero: { noun: "tu lavado" },
  autoescuela: { noun: "tu clase" },
  restauracion: { noun: "tu mesa" },
  barberia: { noun: "tu cita" },
  peluqueria: { noun: "tu cita" },
  estetica: { noun: "tu cita" },
  canina: { noun: "tu cita" },
};

export function getAction(sector) {
  if (FOOD_SECTORS.has(String(sector || "").toLowerCase().trim())) {
    return { noun: "tu pedido", food: true };
  }
  return ACTION_BY_SECTOR[sectorKey(sector)] || { noun: "tu cita" };
}

// Prefijo de la etiqueta con la que pedimos al cliente final que guarde el número
// compartido de Conext. Espejo de SAVE_PREFIX_KEY_BY_SECTOR de appointments; los
// sectores de comida usan "Pedidos" (aquí el simulador sí los distingue).
// DECISIÓN (review final 2026-07-28, Important 1): el simulador NO replica el
// reparto de messageVariants.js/sectorFamily (que da "Reservas" para los 7
// sectores de comida). Aquí esos sectores renderizan el producto de PEDIDOS, no
// el de citas, así que "Pedidos" es la etiqueta correcta para ESTE simulador. No
// "arreglar" esto para hacerlo idéntico al backend de citas — sería el bug al
// revés.
const SAVE_PREFIX_BY_SECTOR = {
  lavadero: "Lavados",
  autoescuela: "Clases",
  restauracion: "Reservas",
};

export function getSaveContactLine(sector, companyName) {
  const name = String(companyName || "").replace(/\s+/g, " ").trim();
  if (!name) return "";
  const food = FOOD_SECTORS.has(String(sector || "").toLowerCase().trim());
  const prefix = food ? "Pedidos" : SAVE_PREFIX_BY_SECTOR[sectorKey(sector)] || "Citas";
  return `📲 Guárdanos como «${prefix} ${name}» para tenernos siempre a mano.`;
}

// Mensaje completo listo para pegar en una lista de difusión de WhatsApp y
// enviar a todos los clientes. Incluye el enlace (fricción 0).
export function getBroadcastMessage(sector, url, companyName) {
  const { noun, food } = getAction(sector);
  const verb = food ? "pedir" : "reservar";
  const base = `¡Hola! 👋 Ahora puedes ${verb} ${noun} por WhatsApp cuando quieras, sin llamadas ni esperas. ${
    food ? "Haz tu pedido" : "Reserva"
  } aquí 👇\n\n${url || ""}`.trim();
  const save = getSaveContactLine(sector, companyName);
  return save ? `${base}\n\n${save}` : base;
}

// Caption sugerido para publicar el enlace en Instagram/Facebook (post o historia).
export function getSocialCaption(sector) {
  const { noun, food } = getAction(sector);
  const verb = food ? "pedir" : "reservar";
  return `📅 ¡Ya puedes ${verb} ${noun} por WhatsApp! Toca el enlace de nuestra bio y ${
    food ? "haz tu pedido" : "pide cita"
  } cuando quieras.`;
}

// Mensaje para la respuesta automática (mensaje de bienvenida) de WhatsApp
// Business: se envía solo cuando un cliente escribe por primera vez. Incluye el
// enlace (fricción 0).
export function getAutoReplyMessage(sector, url, companyName) {
  const { noun, food } = getAction(sector);
  const action = food ? "hacer tu pedido" : `reservar ${noun}`;
  const base = `¡Hola! 👋 Gracias por escribirnos. Puedes ${action} al instante desde aquí 👇\n\n${url || ""}`.trim();
  const save = getSaveContactLine(sector, companyName);
  return save ? `${base}\n\n${save}` : base;
}

// Nombre de archivo del PNG descargado: cartel-whatsapp-<slug>.png
export function posterFilename(companyName) {
  const slug = String(companyName || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `cartel-whatsapp-${slug || "negocio"}.png`;
}
